<template>
  <div class="page-shell business-detail-page">
    <div class="topbar business-detail-page__topbar">
      <div class="page-title business-detail-page__title">
        <h1><span class="title-icon">🤝</span>商业合作申请详情</h1>
        <p>查看申请信息并处理待处理记录</p>
      </div>
      <button class="btn btn-outline" type="button" @click="backToList">返回列表</button>
    </div>

    <div v-if="loading" class="card business-detail-card business-detail-card--state">
      加载中...
    </div>

    <div v-else-if="currentItem" class="card business-detail-card">
      <section class="detail-section">
        <h3 class="detail-section__title">基本信息</h3>
        <div class="detail-grid">
          <div class="detail-field"><span>申请编号</span><strong class="detail-field__primary">{{ currentItem.applyNo }}</strong></div>
          <div class="detail-field"><span>当前状态</span><strong><span :class="statusClass(currentItem.status)">{{ currentItem.statusText }}</span></strong></div>
          <div class="detail-field"><span>申请人</span><strong>{{ currentItem.name }}</strong></div>
          <div class="detail-field"><span>联系电话</span><strong>{{ currentItem.phone }}</strong></div>
          <div class="detail-field"><span>身份类型</span><strong>{{ currentItem.roleTypeText }}</strong></div>
          <div class="detail-field"><span>所属机构</span><strong>{{ currentItem.organization || '—' }}</strong></div>
          <div class="detail-field"><span>提交 IP</span><strong>{{ currentItem.ip || '—' }}</strong></div>
          <div class="detail-field"><span>提交时间</span><strong>{{ currentItem.createdAt }}</strong></div>
          <div class="detail-field"><span>更新时间</span><strong>{{ currentItem.updatedAt }}</strong></div>
        </div>
      </section>

      <section class="detail-section">
        <h3 class="detail-section__title">需求详情</h3>
        <div class="demand-card">
          <div class="demand-card__label">需求类型</div>
          <div class="demand-tags">
            <span v-for="need in currentItem.demandTypesText" :key="need" class="demand-tag">{{ need }}</span>
            <span v-if="!currentItem.demandTypesText.length" class="demand-tag demand-tag--muted">暂无</span>
          </div>
        </div>
        <div class="demand-card">
          <div class="demand-card__label">需求说明</div>
          <div class="demand-card__text">{{ currentItem.description || '暂无' }}</div>
        </div>
      </section>

      <section class="detail-section detail-section--last">
        <h3 class="detail-section__title">处理操作</h3>

        <div v-if="isReadonly" class="review-result">
          <div class="review-result__label">处理结果</div>
          <div class="review-result__status">
            <span :class="statusClass(currentItem.status)">{{ currentItem.statusText }}</span>
          </div>
        </div>

        <div v-else class="review-tip">
          确认后将该申请标记为已处理。
        </div>
      </section>

      <div class="detail-modal__footer">
        <button class="btn btn-outline footer-btn" type="button" @click="backToList">返回列表</button>
        <button
          v-if="!isReadonly"
          class="btn btn-success footer-btn"
          type="button"
          :disabled="submitting"
          @click="submitProcess"
        >
          {{ submitting ? '处理中...' : '确认处理' }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import {
  cooperationApplicationDetail,
  cooperationApplicationProcess
} from '../../../api/userMG'

export default {
  name: 'business-application-detail',
  data() {
    return {
      loading: false,
      submitting: false,
      currentItem: null
    }
  },
  computed: {
    isReadonly() {
      return !this.currentItem || this.currentItem.status !== 'pending'
    }
  },
  created() {
    this.loadDetail()
  },
  watch: {
    '$route.params.id'() {
      this.loadDetail()
    }
  },
  methods: {
    async loadDetail() {
      this.loading = true
      this.currentItem = null

      try {
        const res = await cooperationApplicationDetail(this.$route.params.id)
        const detail = this.extractDetail(res)

        if (!detail || !detail.id) {
          this.$message.error(this.getErrorMessage(res, '未找到对应的商业合作申请'))
          this.backToList()
          return
        }

        this.currentItem = this.normalizeItem(detail)
      } catch (error) {
        this.$message.error(this.getErrorMessage(error, '商业合作申请详情加载失败'))
        this.backToList()
      } finally {
        this.loading = false
      }
    },
    extractDetail(res) {
      if (!res || typeof res !== 'object' || res.message || res.detail || res.errors) {
        return null
      }

      if (res.data && !Array.isArray(res.data) && typeof res.data === 'object') {
        return res.data
      }

      return res
    },
    normalizeItem(item) {
      return {
        id: item.id,
        applyNo: item.apply_no,
        name: item.name,
        phone: item.phone,
        roleTypeText: item.role_type_text || item.role_type || '—',
        organization: item.organization,
        demandTypesText: Array.isArray(item.demand_types_text) ? item.demand_types_text : [],
        description: item.description,
        status: item.status,
        statusText: item.status_text || this.statusText(item.status),
        ip: item.ip,
        createdAt: item.created_at || '—',
        updatedAt: item.updated_at || '—'
      }
    },
    statusText(status) {
      return {
        pending: '待处理',
        processed: '已处理'
      }[status] || status
    },
    statusClass(status) {
      return {
        status: true,
        'status-pending': status === 'pending',
        'status-approved': status === 'processed',
        'status-rejected': status === 'rejected'
      }
    },
    getErrorMessage(error, fallback) {
      if (!error) return fallback
      if (typeof error === 'string') return error
      if (error.message) return error.message
      if (error.detail) return error.detail
      if (error.errors) {
        const firstKey = Object.keys(error.errors)[0]
        const firstError = firstKey ? error.errors[firstKey] : ''

        if (Array.isArray(firstError)) {
          return firstError[0] || fallback
        }

        return firstError || fallback
      }

      return fallback
    },
    async submitProcess() {
      try {
        await this.$confirm('确认将该申请标记为已处理吗？', '提示', {
          type: 'warning'
        })
      } catch (error) {
        return
      }

      this.submitting = true

      try {
        const res = await cooperationApplicationProcess(this.currentItem.id, {
          status: 'processed'
        })

        if (this.isRequestFailed(res)) {
          this.$message.error(this.getErrorMessage(res, '申请处理失败'))
          return
        }

        this.$message.success('申请已处理')
        await this.loadDetail()
      } catch (error) {
        this.$message.error(this.getErrorMessage(error, '申请处理失败'))
      } finally {
        this.submitting = false
      }
    },
    isRequestFailed(res) {
      return typeof res === 'string' || !!(res && typeof res === 'object' && (res.message || res.detail || res.errors))
    },
    backToList() {
      this.$router.push({
        path: '/applications/business',
        query: Object.assign({}, this.$route.query)
      })
    }
  }
}
</script>

<style scoped>
.business-detail-page {
  gap: 18px;
}

.business-detail-page__topbar {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  min-height: 70px;
  margin: -20px -20px 0;
  padding: 18px 24px 20px;
  background: #e9eef5;
}

.business-detail-page__title h1 {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 18px;
  line-height: 1.2;
}

.business-detail-page__title p {
  margin-top: 10px;
  font-size: 13px;
  color: #334155;
}

.title-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  font-size: 18px;
}

.business-detail-card {
  padding: 24px;
  border-radius: 16px;
}

.business-detail-card--state {
  text-align: center;
  color: var(--cog-text-secondary);
}

.detail-section + .detail-section {
  margin-top: 24px;
}

.detail-section--last {
  margin-bottom: 8px;
}

.detail-section__title {
  margin: 0 0 14px;
  padding-bottom: 10px;
  border-bottom: 1px solid #e5e7eb;
  font-size: 15px;
  font-weight: 700;
  color: #1e293b;
}

.detail-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px 28px;
}

.detail-field {
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
}

.detail-field span {
  font-size: 13px;
  color: #94a3b8;
}

.detail-field strong {
  font-size: 14px;
  font-weight: 500;
  color: #1e293b;
  line-height: 1.5;
  word-break: break-word;
}

.detail-field__primary {
  color: #4f46e5;
}

.demand-card {
  padding: 16px 18px;
  background: #f1f5f9;
  border-radius: 12px;
}

.demand-card + .demand-card {
  margin-top: 10px;
}

.demand-card__label {
  margin-bottom: 12px;
  font-size: 14px;
  font-weight: 700;
  color: #4f46e5;
}

.demand-card__text {
  font-size: 14px;
  line-height: 1.7;
  color: #1e293b;
  white-space: pre-wrap;
  word-break: break-word;
}

.demand-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 10px 12px;
}

.demand-tag {
  display: inline-flex;
  align-items: center;
  min-height: 30px;
  padding: 0 12px;
  background: rgba(79, 70, 229, 0.1);
  border-radius: 999px;
  font-size: 13px;
  font-weight: 600;
  color: #4f46e5;
}

.demand-tag--muted {
  background: #e2e8f0;
  color: #64748b;
}

.review-result__label {
  margin-bottom: 8px;
  font-size: 14px;
  font-weight: 700;
  color: #1e293b;
}

.review-tip {
  padding: 14px 16px;
  background: #f8fafc;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  font-size: 14px;
  line-height: 1.7;
  color: #334155;
}

.detail-modal__footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 24px;
}

.footer-btn {
  min-width: 108px;
}

@media (max-width: 768px) {
  .business-detail-page__topbar {
    flex-direction: column;
    align-items: stretch;
    margin: -16px -16px 0;
    padding: 16px;
  }

  .business-detail-card {
    padding: 16px;
  }

  .detail-grid {
    grid-template-columns: 1fr;
    gap: 12px;
  }

  .detail-modal__footer {
    flex-direction: column-reverse;
  }

  .footer-btn {
    width: 100%;
  }
}
</style>



// WEBPACK FOOTER //
// src/views/applications/business/detail.vue